import React, { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

const CREATE_SPRINT = gql`
  mutation CreateSprint($name: String, $objective_text: String, $start_on: ISO8601Date, $end_on: ISO8601Date, $status: SprintStatusEnum) {
    create_sprint(name: $name, objective_text: $objective_text, start_on: $start_on, end_on: $end_on, status: $status) {
      id
      name
      status
    }
  }
`;

const UPDATE_SPRINT = gql`
  mutation UpdateSprint($id: ID!, $name: String, $objective_text: String, $start_on: ISO8601Date, $end_on: ISO8601Date, $status: SprintStatusEnum) {
    update_sprint(id: $id, name: $name, objective_text: $objective_text, start_on: $start_on, end_on: $end_on, status: $status) {
      id
      name
      status
    }
  }
`;

const CHECK_ACTIVE_SPRINT = gql`
  query CheckActiveSprint {
    sprints(status: [ACTIVE]) {
      id
    }
  }
`;

const SprintCreateModal = ({ isOpen, onClose, onCreated, onUpdated, ownerName, sprint }) => {
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('');
  const [startOn, setStartOn] = useState('');
  const [endOn, setEndOn] = useState('');
  const [status, setStatus] = useState('PLANNED');
  const [errors, setErrors] = useState({ name: '', objective: '', startOn: '', endOn: '' });

  const [createSprint, { loading: creating, error: createError }] = useMutation(CREATE_SPRINT);
  const [updateSprint, { loading: updating, error: updateError }] = useMutation(UPDATE_SPRINT);
  const { data: activeSprintData } = useQuery(CHECK_ACTIVE_SPRINT, { skip: !isOpen });
  
  const isEditMode = !!sprint;
  const loading = creating || updating;
  const error = createError || updateError;
  
  // When editing, allow setting to ACTIVE only if the sprint being edited is the active one or there's no active sprint
  const hasOtherActiveSprint = (activeSprintData?.sprints || []).some(s => s.id !== sprint?.id);

  useEffect(() => {
    if (!isOpen) return;
    
    if (sprint) {
      // Edit mode: pre-fill with sprint data
      setName(sprint.name || '');
      setObjective(sprint.objectiveText || sprint.objective_text || '');
      setStartOn(sprint.startOn || sprint.start_on || '');
      setEndOn(sprint.endOn || sprint.end_on || '');
      setStatus(sprint.status?.toUpperCase() || 'PLANNED');
    } else {
      // Create mode: use defaults
      const today = new Date();
      const defaultStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      const defaultEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
      setName('');
      setObjective('');
      setStartOn(defaultStart.toISOString().slice(0, 10));
      setEndOn(defaultEnd.toISOString().slice(0, 10));
      setStatus('PLANNED');
    }
    setErrors({ name: '', objective: '', startOn: '', endOn: '' });
  }, [isOpen, sprint]);

  if (!isOpen) return null;

  const validate = () => {
    const next = { name: '', objective: '', startOn: '', endOn: '' };
    if (!name.trim()) next.name = 'Sprint name is required';
    if (!objective.trim()) next.objective = 'Sprint objective is required';
    if (!startOn) next.startOn = 'Start date is required';
    if (!endOn) next.endOn = 'End date is required';
    if (startOn && endOn) {
      const s = new Date(startOn);
      const e = new Date(endOn);
      if (s >= e) next.endOn = 'End date must be after start date';
    }
    setErrors(next);
    return !next.name && !next.objective && !next.startOn && !next.endOn;
  };

  const durationLabel = () => {
    if (!startOn || !endOn) return '';
    const s = new Date(startOn);
    const e = new Date(endOn);
    if (e <= s) return '';
    const diffDays = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return '1 day';
    if (diffDays < 7) return `${diffDays} days`;
    if (diffDays < 30) return `${Math.round(diffDays / 7)} weeks`;
    return `${Math.round(diffDays / 30)} months`;
  };

  const submit = async () => {
    if (loading) return; // Prevent double-clicks
    if (!validate()) return;
    
    try {
      if (isEditMode) {
        // Update existing sprint
        const res = await updateSprint({
          variables: {
            id: sprint.id,
            name: name || null,
            objective_text: objective || null,
            start_on: startOn || null,
            end_on: endOn || null,
            status: status || null,
          },
        });
        if (res?.data?.update_sprint && onUpdated) onUpdated(res.data.update_sprint);
      } else {
        // Create new sprint
        const res = await createSprint({
          variables: {
            name: name || null,
            objective_text: objective || null,
            start_on: startOn || null,
            end_on: endOn || null,
            status: status || null,
          },
        });
        const newSprint = res?.data?.create_sprint;
        if (newSprint && onCreated) onCreated(newSprint);
      }
    } catch (err) {
      // Error is handled by the useMutation error state
      console.error('Failed to save sprint:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
              <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{isEditMode ? 'Edit Sprint' : 'Create New Sprint'}</h3>
              <p className="text-sm text-gray-500">{isEditMode ? 'Update sprint details' : 'Define sprint goals and timeline'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error.message}</div>}

          {/* Name */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Sprint Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Q1 Value Acceleration"
              className={`mt-1 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Objective */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Sprint Objective *</label>
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              placeholder="Describe the main goals and outcomes for this sprint..."
              rows={2}
              className={`mt-1 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${errors.objective ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
            />
            {errors.objective && <p className="text-sm text-red-600">{errors.objective}</p>}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Start Date *</label>
              <div className="relative">
                <input type="date" value={startOn} onChange={(e) => setStartOn(e.target.value)} className={`w-full rounded-md border px-3 py-2 pr-9 focus:outline-none focus:ring-2 ${errors.startOn ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} />
                <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              {errors.startOn && <p className="text-sm text-red-600">{errors.startOn}</p>}
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">End Date *</label>
              <div className="relative">
                <input type="date" value={endOn} onChange={(e) => setEndOn(e.target.value)} className={`w-full rounded-md border px-3 py-2 pr-9 focus:outline-none focus:ring-2 ${errors.endOn ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} />
                <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              {errors.endOn && <p className="text-sm text-red-600">{errors.endOn}</p>}
            </div>
          </div>

          {/* Duration */}
          {durationLabel() && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <span className="font-medium">Duration:</span> {durationLabel()}
            </div>
          )}

          {/* Status and Owner */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="PLANNED">Planned</option>
                <option value="ACTIVE" disabled={hasOtherActiveSprint}>Active {hasOtherActiveSprint ? '(already have an active sprint)' : ''}</option>
                {isEditMode && <option value="CLOSED">Closed</option>}
                {isEditMode && <option value="ABANDONED">Abandoned</option>}
              </select>
              {hasOtherActiveSprint && !isEditMode && (
                <p className="text-xs text-amber-600">You already have an active sprint. Close it before starting a new one.</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Sprint Owner</label>
              <div className="relative">
                <input value={ownerName || ''} readOnly className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700" />
                <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"/><path d="M6 20c0-3.31 2.69-6 6-6s6 2.69 6 6"/></svg>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t bg-gray-50 px-6 py-3">
          <button onClick={onClose} className="rounded-md border px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={loading} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {loading ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save Sprint' : 'Create Sprint')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SprintCreateModal;


